"use client"

import * as React from "react"

import { Button } from "@/components/ui/button"
import {
  CircleHelpIcon,
  type CircleHelpIconHandle,
} from "@/components/ui/circle-help"
import {
  MessageCircleIcon,
  type MessageCircleIconHandle,
} from "@/components/ui/message-circle-icon"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useSidebar } from "@/components/ui/sidebar"
import { cn } from "@/lib/utils"

const HELP_CENTRE_URL = "https://help.tesseratrades.com/"
/** Update when a dedicated feedback form URL is ready. */
const SUGGESTION_URL = "https://help.tesseratrades.com/"

const expandedButtonClass = cn(
  "min-w-0 flex-1 items-center justify-center gap-1.5 leading-none"
)

const collapsedIconButtonClass = cn(
  "size-8! min-w-8! max-w-8! shrink-0 items-center justify-center gap-0! p-0! leading-none"
)

const iconShellClass =
  "inline-flex size-4 shrink-0 items-center justify-center leading-none text-current"

const labelClass = "leading-none normal-case tracking-normal"

function useIconHoverHandlers(start: () => void, stop: () => void) {
  return React.useMemo(
    () => ({
      onPointerDownCapture: start,
      onMouseEnter: start,
      onMouseLeave: stop,
    }),
    [start, stop]
  )
}

function openExternal(url: string) {
  window.open(url, "_blank", "noopener,noreferrer")
}

function SidebarHelpButton({ collapsed }: { collapsed: boolean }) {
  const iconRef = React.useRef<CircleHelpIconHandle | null>(null)

  const start = React.useCallback(() => {
    void iconRef.current?.startAnimation()
  }, [])
  const stop = React.useCallback(() => {
    void iconRef.current?.stopAnimation()
  }, [])
  const hoverHandlers = useIconHoverHandlers(start, stop)

  const button = (
    <Button
      type="button"
      variant="outline-brand"
      size="sm"
      data-font="primary"
      data-need-help-button=""
      data-testid="shadcn-sidebar-help-centre"
      aria-label={collapsed ? "Need Help?" : undefined}
      className={collapsed ? collapsedIconButtonClass : expandedButtonClass}
      {...hoverHandlers}
      onClick={() => openExternal(HELP_CENTRE_URL)}
    >
      <CircleHelpIcon
        ref={iconRef}
        data-icon="inline-start"
        size={16}
        className={iconShellClass}
        aria-hidden
      />
      {!collapsed ? <span className={labelClass}>Need Help?</span> : null}
    </Button>
  )

  if (!collapsed) return button

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="right" align="center">
        Need Help?
      </TooltipContent>
    </Tooltip>
  )
}

function SidebarSuggestionButton({ collapsed }: { collapsed: boolean }) {
  const iconRef = React.useRef<MessageCircleIconHandle | null>(null)

  const start = React.useCallback(() => {
    void iconRef.current?.startAnimation()
  }, [])
  const stop = React.useCallback(() => {
    void iconRef.current?.stopAnimation()
  }, [])
  const hoverHandlers = useIconHoverHandlers(start, stop)

  const button = (
    <Button
      type="button"
      variant="outline-brand"
      size="sm"
      data-font="primary"
      data-testid="shadcn-sidebar-suggestion"
      aria-label={collapsed ? "Feedback?" : undefined}
      className={collapsed ? collapsedIconButtonClass : expandedButtonClass}
      {...hoverHandlers}
      onClick={() => openExternal(SUGGESTION_URL)}
    >
      <MessageCircleIcon
        ref={iconRef}
        data-icon="inline-start"
        size={16}
        className={iconShellClass}
        aria-hidden
      />
      {!collapsed ? <span className={labelClass}>Feedback?</span> : null}
    </Button>
  )

  if (!collapsed) return button

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="right" align="center">
        Feedback?
      </TooltipContent>
    </Tooltip>
  )
}

/**
 * Footer help + suggestion CTAs in one row above Resync Trades.
 * Real buttons (not anchors) so outline-brand centering matches Resync / Need Help.
 */
export function SidebarSupportButtons() {
  const { isMobile, state } = useSidebar()
  const collapsed = state === "collapsed" && !isMobile

  return (
    <div
      className={cn(
        "flex w-full max-w-full items-center gap-2",
        collapsed && "flex-col"
      )}
    >
      <SidebarHelpButton collapsed={collapsed} />
      <SidebarSuggestionButton collapsed={collapsed} />
    </div>
  )
}
